export const RESALE_SECTION_KEYS = [
  'engine',
  'transmission',
  'chassis',
  'paint_thickness',
  'computer_diagnosis',
  'suspension',
  'tires',
  'air_conditioning',
  'electrical',
];

export const RESALE_SECTION_LABELS = {
  engine: 'Engine',
  transmission: 'Transmission',
  chassis: 'Chassis',
  paint_thickness: 'Paint thickness',
  computer_diagnosis: 'Computer diagnosis',
  suspension: 'Suspension',
  tires: 'Tires',
  air_conditioning: 'Air conditioning',
  electrical: 'Electrical systems',
};

export const RESALE_STATUS_OPTIONS = [
  { value: 'good', label: 'Good' },
  { value: 'fair', label: 'Fair' },
  { value: 'poor', label: 'Poor' },
  { value: 'critical', label: 'Critical' },
];

export const MARKET_CONDITION_LABELS = {
  excellent: 'Excellent',
  good: 'Good',
  fair: 'Fair',
  poor: 'Poor',
  needs_major_work: 'Needs major work',
};

const WEIGHTS = {
  engine: 1.4,
  transmission: 1.3,
  chassis: 1.2,
  computer_diagnosis: 1.1,
};

export function statusPenalty(status) {
  const value = (status || '').toLowerCase();
  if (value === 'critical') return 55;
  if (value === 'poor') return 35;
  if (value === 'fair') return 15;
  return 0;
}

function clampScore(value) {
  return Math.max(0, Math.min(100, Math.round(value)));
}

export function suggestedScoreForStatus(status) {
  return clampScore(100 - statusPenalty(status));
}

export function sectionScore(section = {}) {
  const base = Number(section.score);
  if (Number.isFinite(base)) return clampScore(base);
  return suggestedScoreForStatus(section.status);
}

export function normalizeResaleSection(section = {}) {
  const status = section.status || 'good';
  const suggested = suggestedScoreForStatus(status);
  const scoreManual = section.scoreManual === true;
  const raw = Number(section.score);

  if (scoreManual && Number.isFinite(raw)) {
    return { ...section, status, score: clampScore(raw), scoreManual: true };
  }

  if (Number.isFinite(raw)) {
    const penalty = statusPenalty(status);
    if (penalty > 0 && raw === 100) {
      return { ...section, status, score: suggested, scoreManual: false };
    }
    if (penalty > 0 && raw > suggested) {
      return {
        ...section,
        status,
        score: clampScore(raw - penalty),
        scoreManual: false,
      };
    }
    return { ...section, status, score: clampScore(raw), scoreManual: false };
  }

  return { ...section, status, score: suggested, scoreManual: false };
}

export function normalizeResaleSections(sections = {}) {
  return Object.fromEntries(
    RESALE_SECTION_KEYS.map((key) => [
      key,
      normalizeResaleSection(sections[key]),
    ])
  );
}

export function computeResaleScores(sections = {}) {
  let weightedSum = 0;
  let weightSum = 0;
  let hasCritical = false;

  for (const key of RESALE_SECTION_KEYS) {
    const section = sections[key] || {};
    if ((section.status || '').toLowerCase() === 'critical') {
      hasCritical = true;
    }
    const score = sectionScore(section);
    const weight = WEIGHTS[key] ?? 1;
    weightedSum += score * weight;
    weightSum += weight;
  }

  const overallScore = weightSum > 0 ? clampScore(weightedSum / weightSum) : 100;
  const marketCondition = computeMarketCondition(overallScore, hasCritical);

  return { overallScore, marketCondition, hasCritical };
}

export function computeMarketCondition(overallScore, hasCritical = false) {
  if (hasCritical || overallScore < 40) return 'needs_major_work';
  if (overallScore >= 85) return 'excellent';
  if (overallScore >= 70) return 'good';
  if (overallScore >= 55) return 'fair';
  return 'poor';
}

export function emptyResaleSections() {
  return Object.fromEntries(
    RESALE_SECTION_KEYS.map((key) => [
      key,
      { status: 'good', score: 100, scoreManual: false, notes: '' },
    ])
  );
}

export function scoreBand(score) {
  if (score >= 85) return 'excellent';
  if (score >= 70) return 'good';
  if (score >= 55) return 'fair';
  if (score >= 40) return 'poor';
  return 'critical';
}
